'use client';

import React, { JSX, useEffect, useMemo, useState } from 'react';
import Keys from '@/Translations/generated/da/productTeaser.json.keys';
import { useTranslation } from '@/app/i18n/client';
import { useGlobalMGLProps } from '@/Util/GlobalMGLPropsContext';
import { FROM_SEARCH_PARAMETER, getResultsPageBasePath, logger } from '@/Util/globals';
import Link from '@/components/Link';
import { useCurrentLanguage } from '@/Util/CurrentLanguageProvider';
import { useModelApiClient } from '@/Util/api';
import { type PartialPageProps, SiteConfigParameters } from '@/types/cms/magnolia';
import { getUrlWithUpdatedQueryParams } from '@/components/Search/helpers';
import { usePathname, useSearchParams } from 'next/navigation';
import { ReducerStateItem, ReducerStateItems } from '@/components/Search/searchReducer/types';

interface Props {
  pageProps?: PartialPageProps;
}

const isDestinationPage = (nodePath?: string, parameters?: Partial<SiteConfigParameters>) => {
  return (
    !!nodePath &&
    (nodePath === parameters?.continentDetailsPage ||
      nodePath === parameters?.countryDetailsPage ||
      nodePath === parameters?.cityDetailsPage ||
      Object.values(parameters?.countrySubPages || {}).some(page => page === nodePath) ||
      Object.values(parameters?.citySubPages || {}).some(page => page === nodePath))
  );
};

const TeasersSearchPageButton = (props: Props): JSX.Element => {
  const { t } = useTranslation('productTeaser');
  const { rootNodePath, siteConfig, magnoliaContext } = useGlobalMGLProps() || {};
  const modelApiClient = useModelApiClient();
  const pathname = usePathname();
  const locale = useCurrentLanguage();
  const [productsByMarketCount, setProductsByMarketCount] = useState<number>(0);
  const { pageProps } = props;
  const { travelType, country, continent } = pageProps ?? {};
  const searchParams = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const newCount = await modelApiClient.getProductsByMarketCount(
          siteConfig?.name ?? '',
          siteConfig?.name ?? '',
          locale
        );
        if (newCount) {
          setProductsByMarketCount(newCount);
        }
      } catch {
        logger('error')('Unable to fetch products count');
      }
    })();
  }, [siteConfig, locale]);

  const buttonData = useMemo(() => {
    if (!magnoliaContext?.nodePath) return undefined;
    if (
      magnoliaContext.nodePath === siteConfig?.parameters?.travelTypePage &&
      pathname.split('/').filter(Boolean).length > 1
    ) {
      if (travelType?.id) {
        const travelTypeSearchUrl: Record<string, ReducerStateItem> = {
          [travelType.id]: { name: '', type: 'travelType' }
        };
        return {
          href: `/${getResultsPageBasePath(rootNodePath ?? '', siteConfig)}${getUrlWithUpdatedQueryParams(
            travelTypeSearchUrl,
            searchParams,
            undefined,
            [[FROM_SEARCH_PARAMETER, 'true']]
          )}`,
          label:
            travelType.productTeaserButtonLabel ??
            t(Keys.seeAllTrips, {
              name: travelType.name
            })
        };
      } else {
        return undefined;
      }
    } else if (isDestinationPage(magnoliaContext.nodePath, siteConfig?.parameters))
      if (country?.id && continent?.id) {
        const countrySearchUrl: ReducerStateItems = {
          [continent.id]: {
            name: '',
            type: 'continent',
            countries: { [country.id]: '' }
          }
        };
        return {
          href: `/${getResultsPageBasePath(rootNodePath ?? '', siteConfig)}${getUrlWithUpdatedQueryParams(
            countrySearchUrl,
            searchParams,
            undefined,
            [[FROM_SEARCH_PARAMETER, 'true']]
          )}`,
          label: t(Keys.seeAllTrips, {
            name: country.name
          })
        };
      } else {
        return undefined;
      }
    else {
      return {
        href: `/${getResultsPageBasePath(rootNodePath ?? '', siteConfig)}`,
        label: t(Keys.seeAllTravels, { count: productsByMarketCount })
      };
    }
  }, [
    magnoliaContext,
    rootNodePath,
    siteConfig,
    siteConfig?.parameters,
    productsByMarketCount,
    travelType,
    country,
    continent
  ]);

  return buttonData ? (
    <Link className="btn btn-primary" showCaret href={buttonData.href}>
      {buttonData.label}
    </Link>
  ) : (
    <></>
  );
};

export default TeasersSearchPageButton;
